import React, { useState, useEffect } from 'react';
import { AD } from './admin.constants';
import { s } from './admin.styles';
import { ADIcon } from './ADIcon';
import {
  getToolStatsByName,
  ToolStatsDetailResponse,
  ToolStatsSummary,
  ToolStatsDuration,
  ToolStatsTrend,
  ToolStatsError,
} from 'core/services/admin.service';

interface ToolStatsPanelProps {
  toolName: string;
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + AD.HAIR_SOFT }}>
      <span style={{ fontSize: 12, color: AD.SLATE }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: color || AD.INK, fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}>{value}</span>
    </div>
  );
}

function TrendBar({ item, maxCalls }: { item: ToolStatsTrend; maxCalls: number }) {
  const calls = Number(item.calls) || 0;
  const failures = Number(item.failures) || 0;
  const pct = maxCalls > 0 ? (calls / maxCalls) * 100 : 0;
  const failPct = calls > 0 ? (failures / calls) * 100 : 0;
  const barColor = failPct > 20 ? AD.RED : failPct > 5 ? AD.AMBER : AD.GREEN;
  const rawDay = String(item.day || '');
  const dateLabel = rawDay.length >= 10 ? rawDay.slice(5, 10) : rawDay;
  const callCount = String(calls);
  const failCount = failures > 0 ? ' / ' + failures + ' err' : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span style={{ fontSize: 10, color: AD.SLATE, width: 40, flexShrink: 0, fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}>{dateLabel}</span>
      <div style={{ flex: 1, height: 14, background: AD.HAIR_SOFT, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: barColor, borderRadius: 4, minWidth: pct > 0 ? 2 : 0 }} />
      </div>
      <span style={{ fontSize: 10, color: AD.INK_700, width: 60, flexShrink: 0, textAlign: 'right', fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}>
        {callCount}{failCount}
      </span>
    </div>
  );
}

export function ToolStatsPanel({ toolName }: ToolStatsPanelProps) {
  const [stats, setStats] = useState<ToolStatsDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hours, setHours] = useState(168);

  useEffect(() => {
    setLoading(true);
    setError('');
    getToolStatsByName(toolName, hours)
      .then(data => { setStats(data); setLoading(false); })
      .catch(err => { setError(err instanceof Error ? err.message : 'Failed to load stats'); setLoading(false); });
  }, [toolName, hours]);

  if (loading) {
    return (
      <div style={s.playSec}>
        <div style={{ textAlign: 'center', padding: '24px 0', color: AD.SLATE, fontSize: 12 }}>Loading stats...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.playSec}>
        <div style={{ textAlign: 'center', padding: '24px 0', color: AD.RED, fontSize: 12 }}>
          <ADIcon name="warning" size={20} color={AD.RED} style={{ marginBottom: 8 }} />
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const summaryItem: ToolStatsSummary | undefined = stats.summary && stats.summary[0];
  const durationItem: ToolStatsDuration | undefined = stats.durations && stats.durations[0];
  const errorItems: ToolStatsError[] = stats.errors || [];
  const trendItems: ToolStatsTrend[] = stats.trend || [];

  const totalCalls = summaryItem ? Number(summaryItem.total) || 0 : 0;
  const successCount = summaryItem ? Number(summaryItem.successes) || 0 : 0;
  const failureCount = summaryItem ? Number(summaryItem.failures) || 0 : 0;
  const successRate = totalCalls > 0 ? ((successCount / totalCalls) * 100).toFixed(1) + '%' : '—';

  const avgMs = durationItem ? Number(durationItem.avgMs).toFixed(0) + 'ms' : '—';
  const p50Ms = durationItem ? Number(durationItem.p50Ms).toFixed(0) + 'ms' : '—';
  const p95Ms = durationItem ? Number(durationItem.p95Ms).toFixed(0) + 'ms' : '—';
  const maxMs = durationItem ? Number(durationItem.maxMs).toFixed(0) + 'ms' : '—';

  const maxTrendCalls = trendItems.reduce((m, t) => Math.max(m, Number(t.calls) || 0), 0);

  const hoursLabel = hours === 24 ? '24h' : hours === 168 ? '7d' : hours === 720 ? '30d' : hours + 'h';

  return (
    <>
      {/* Time range selector */}
      <div style={{ ...s.playSec, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ ...s.playLab, marginBottom: 0, flex: 1 }}>Tool stats</span>
        {[24, 168, 720].map(h => {
          const label = h === 24 ? '24h' : h === 168 ? '7d' : '30d';
          const isActive = hours === h;
          const chipStyle = isActive ? { ...s.filterChip, ...s.filterChipActive } : s.filterChip;
          return (
            <button key={h} style={chipStyle} onClick={() => setHours(h)}>{label}</button>
          );
        })}
      </div>

      {/* Summary */}
      <div style={s.playSec}>
        <div style={s.playLab}>{'Summary · ' + hoursLabel}</div>
        <StatRow label="Total calls" value={String(totalCalls)} />
        <StatRow label="Successes" value={String(successCount)} color={AD.GREEN} />
        <StatRow label="Failures" value={String(failureCount)} color={failureCount > 0 ? AD.RED : AD.INK} />
        <StatRow label="Success rate" value={successRate} />
      </div>

      {/* Durations */}
      {durationItem && (
        <div style={s.playSec}>
          <div style={s.playLab}>Latency</div>
          <StatRow label="Avg" value={avgMs} />
          <StatRow label="p50" value={p50Ms} />
          <StatRow label="p95" value={p95Ms} />
          <StatRow label="Max" value={maxMs} />
        </div>
      )}

      {/* Errors */}
      {errorItems.length > 0 && (
        <div style={s.playSec}>
          <div style={s.playLab}>{'Errors · ' + errorItems.length}</div>
          {errorItems.map((e, i) => {
            const code = String(e.errorCode || 'unknown');
            const count = String(e.count);
            return <StatRow key={i} label={code} value={count} color={AD.RED} />;
          })}
        </div>
      )}

      {/* Daily trend */}
      {trendItems.length > 0 && (
        <div style={s.playSec}>
          <div style={s.playLab}>Daily trend</div>
          {trendItems.map((t, i) => (
            <TrendBar key={i} item={t} maxCalls={maxTrendCalls} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {totalCalls === 0 && trendItems.length === 0 && (
        <div style={s.playSec}>
          <div style={{ textAlign: 'center', padding: '12px 0', color: AD.SLATE, fontSize: 12 }}>
            No calls recorded in the last {hoursLabel}.
          </div>
        </div>
      )}
    </>
  );
}
